import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Barcode, Copy, Check, DollarSign } from 'lucide-react';
import { ProTag } from '@/components/ui/pro-tag';
import { useFeature } from '@/hooks/useFeature';
import { useBarcodeScanner, BarcodeFormat } from '@/hooks/useBarcodeScanner';
import { formatBoletoBarcode, isBoletoBarcode, parseBoletoBarcode, type BoletoInfo } from '@/lib/barcodeUtils';
import type { FinanceEntryPrefill } from '@/pages/FinanceEntryFormPage';
import { todayIsoDate } from '@/lib/financeStatus';
import { toast } from 'sonner';

interface BoletoScannerButtonProps {
  className?: string;
  /** Mostra o CTA "Lançar no financeiro" no diálogo (usado na página Financeiro). */
  enableFinanceEntry?: boolean;
  /**
   * Só o ícone, em QUALQUER largura — para o hero do desenho oficial, onde o
   * botão é um círculo de 36px e o rótulo `sm:inline` vazaria dele a partir de
   * 640px. O nome continua no `aria-label`/`title` (#142), e o selo PRO vira
   * um distintivo no canto em vez de um item na linha.
   */
  iconOnly?: boolean;
}

export function BoletoScannerButton({
  className,
  enableFinanceEntry = false,
  iconOnly = false,
}: BoletoScannerButtonProps) {
  const navigate = useNavigate();
  const boletoScanner = useFeature('boleto_scanner');
  const { scan, isAvailable, isScanning } = useBarcodeScanner();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linhaDigitavel, setLinhaDigitavel] = useState<string | null>(null);
  const [boletoInfo, setBoletoInfo] = useState<BoletoInfo | null>(null);
  const [copied, setCopied] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);

  const showButton = isAvailable || import.meta.env.DEV;
  if (!showButton) return null;

  const showFinanceCta = enableFinanceEntry;

  const handleClick = async () => {
    if (!boletoScanner.require()) return;
    const code = await scan([BarcodeFormat.Itf]);
    if (!code) return;
    if (!isBoletoBarcode(code)) {
      toast.warning('Código lido não parece ser um boleto (44 dígitos).');
      return;
    }
    const linha = formatBoletoBarcode(code);
    if (!linha) {
      toast.error('Não foi possível formatar a linha digitável.');
      return;
    }
    setLinhaDigitavel(linha);
    setBoletoInfo(parseBoletoBarcode(code));
    setCopied(false);
    setDialogOpen(true);
  };

  const handleCopy = async () => {
    if (!linhaDigitavel) return;
    try {
      await navigator.clipboard.writeText(linhaDigitavel.replace(/\s/g, ''));
      setCopied(true);
      toast.success('Linha digitável copiada!');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  const financePrefill: FinanceEntryPrefill = {
    kind: 'expense',
    amount: boletoInfo?.amount ?? undefined,
    due_date: boletoInfo?.dueDate ?? todayIsoDate(),
    payment_method: 'boleto',
    description: 'Pagamento de boleto',
    notes: linhaDigitavel ? `Linha digitável: ${linhaDigitavel}` : null,
  };

  const amountLabel =
    boletoInfo?.amount != null
      ? boletoInfo.amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : null;

  const dueDateLabel = boletoInfo?.dueDate
    ? new Date(`${boletoInfo.dueDate}T00:00:00`).toLocaleDateString('pt-BR')
    : null;

  return (
    <>
      {/* Em 360px este botão divide o cabeçalho do Financeiro com "Exportar" e
          "Novo": com o rótulo sempre visível as três ações somavam 367px para
          328 disponíveis e o "Novo" saía INTEIRO da tela (medido no aparelho).
          Abaixo de `sm` fica só o ícone — com `aria-label`, senão o botão perde
          o nome acessível junto com o texto (#142). */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={className}
        onClick={handleClick}
        disabled={isScanning}
        aria-label="Escanear boleto"
        title="Escanear boleto"
      >
        <Barcode className={iconOnly ? 'h-4 w-4' : 'h-4 w-4 sm:mr-2'} />
        {!iconOnly && <span className="hidden sm:inline">Escanear boleto</span>}
        {!boletoScanner.allowed &&
          (iconOnly ? (
            <ProTag className="absolute -right-1 -top-1" />
          ) : (
            <ProTag className="ml-1.5" />
          ))}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Linha digitável do boleto</DialogTitle>
            <DialogDescription>
              Confira e copie para pagamento.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted/50 p-3 font-mono text-sm break-all select-all">
            {linhaDigitavel}
          </div>
          {(amountLabel || dueDateLabel) && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
              {amountLabel && (
                <span>Valor: <strong className="text-foreground">{amountLabel}</strong></span>
              )}
              {dueDateLabel && (
                <span>Vencimento: <strong className="text-foreground">{dueDateLabel}</strong></span>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            {showFinanceCta && (
              <Button3D
                color="green"
                onClick={() => {
                  setDialogOpen(false);
                  navigate('/financeiro/novo', { state: { prefill: financePrefill, defaultKind: 'expense' } });
                }}
              >
                <DollarSign className="mr-2 h-4 w-4" />
                Lançar no financeiro
              </Button3D>
            )}
            <Button3D color="gray" onClick={() => setDialogOpen(false)}>
              Fechar
            </Button3D>
            <Button3D color="green" onClick={handleCopy}>
              {copied ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copiar
                </>
              )}
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}
